import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useFocusEffect } from '@react-navigation/native';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Platform,
  ActivityIndicator,
  Modal,
  Pressable,
  FlatList,
  Linking,
  useWindowDimensions,
  Image as RNImage,
} from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { Image } from 'expo-image';
import RenderHtml from 'react-native-render-html';
import { useTheme } from '../context/ThemeContext';
import HamburgerMenu from '../components/HamburgerMenu';
import ProfileIcon from '../components/ProfileIcon';
import Stopwatch from '../components/Stopwatch';
import CourseMediaCarousel, { createCourseMediaCarouselStyles } from '../components/CourseMediaCarousel';
import ModalVideoEmbed from '../components/ModalVideoEmbed';
import { getApiUrl } from '../utils/apiConfig';
import { resolveCourseGalleryEntryToUrl, isSupabasePublicVideoUrl } from '../utils/courseMediaUrls';
import { studentHasCourseName } from '../utils/studentCourses';
import { hasSuperUserAccess } from '../utils/sessionUser';
import { isFirstDegreeOrHigher } from '../utils/gradeAccess';
import MaterialIcons from '@expo/vector-icons/MaterialIcons';

const CAROUSEL_ASPECT = 16 / 9;
const CAROUSEL_WIDTH_RATIO = 0.6;
const CAROUSEL_ARROW_HIT = 48;
const EXT_THUMB_TILE_WIDTH = 92;
const EXT_THUMB_TILE_GAP = 10;
/** Light mode locked-course label; dark mode uses {@link COURSE_LOCKED_YELLOW}. */
const COURSE_LOCKED_GOLD = '#d4af37';
const COURSE_LOCKED_YELLOW = '#ffd700';

/** Resource links under Course breaks (e.g. handbook). Omit entries until URLs exist — no placeholders. */
const COURSE_RESOURCE_LINKS = [];

/** Align with MyCourses navigation (`collapseSpaces`) so titles match DB names that vary only by whitespace. */
function normalizeCourseDisplayName(s) {
  return String(s ?? '').trim().replace(/\s+/g, ' ');
}

function viewerHasCourseEnrollment(user, courseName) {
  if (!user?.id) return false;
  if (hasSuperUserAccess(user)) return true;
  return studentHasCourseName(user, courseName);
}

function showChiGungHandbookOnCourseDetail(user, courseName) {
  if (!viewerHasCourseEnrollment(user, courseName)) return false;
  const n = normalizeCourseDisplayName(courseName);
  return n.localeCompare('Chi Gung', undefined, { sensitivity: 'base' }) === 0;
}

function showDimMakHandbookOnCourseDetail(user, courseName) {
  if (!viewerHasCourseEnrollment(user, courseName)) return false;
  const n = normalizeCourseDisplayName(courseName);
  return n.localeCompare('Dim Mak', undefined, { sensitivity: 'base' }) === 0;
}

function canCreateCourseStories(user) {
  if (!user?.id) return false;
  if (hasSuperUserAccess(user)) return true;
  return isFirstDegreeOrHigher(user);
}

function computeCourseCompletionPercent(breaks) {
  if (!Array.isArray(breaks) || breaks.length === 0) return 0;
  const done = breaks.filter((b) => String(b.status).toLowerCase() === 'completed').length;
  return Math.round((done / breaks.length) * 100);
}

/** @returns {string | null} */
function parseYouTubeVideoId(url) {
  if (!url || typeof url !== 'string') return null;
  const s = url.trim();
  const m =
    s.match(
      /(?:https?:\/\/)?(?:(?:www|m)\.)?youtube\.com\/(?:watch\?v=|embed\/|shorts\/)([^&?#/]+)/i
    ) || s.match(/(?:https?:\/\/)?youtu\.be\/([^&?#/]+)/i);
  return m ? m[1] : null;
}

function youtubeEmbedUrl(videoId) {
  if (!videoId) return '';
  const q = 'autoplay=1&playsinline=1&rel=0';
  return `https://www.youtube.com/embed/${encodeURIComponent(videoId)}?${q}`;
}

function youtubeThumbUrl(videoId) {
  if (!videoId) return '';
  return `https://i.ytimg.com/vi/${encodeURIComponent(videoId)}/hqdefault.jpg`;
}

/** YouTube → embed URL for modal; otherwise return URL as-is for WebView. */
function resolvePlaybackUri(url) {
  if (!url || typeof url !== 'string') return '';
  const id = parseYouTubeVideoId(url);
  if (id) return youtubeEmbedUrl(id);
  return url.trim();
}

/** Thumbnail / full-screen: site images and /assets paths (not YouTube or direct video). */
function isLikelyImageUrl(url) {
  if (!url || typeof url !== 'string') return false;
  if (parseYouTubeVideoId(url)) return false;
  if (isSupabasePublicVideoUrl(url)) return false;
  const noQuery = url.split('?')[0].toLowerCase();
  if (/\.(mp4|webm|mov|m4v|mpeg|mpg)$/i.test(noQuery)) return false;
  const path = url.split('?')[0].toLowerCase();
  if (path.includes('/assets/')) return true;
  return /\.(jpe?g|png|gif|webp)$/i.test(path.trim());
}

function resolveExternalPlaybackUri(url) {
  if (!url || typeof url !== 'string') return '';
  if (parseYouTubeVideoId(url)) return resolvePlaybackUri(url);
  return resolveCourseMediaUri(url);
}

function imageFileLabelFromUrl(url) {
  if (!url || typeof url !== 'string') return '';
  const noQuery = url.split('?')[0];
  const parts = noQuery.split('/');
  const seg = parts.filter(Boolean).pop() || '';
  if (!seg) return '';
  try {
    return decodeURIComponent(seg.replace(/\+/g, ' '));
  } catch {
    return seg;
  }
}

/** Under-thumbnail caption: break name for video; file name from path for images. */
function externalLinkStripLabel(row) {
  if (parseYouTubeVideoId(row.url)) {
    return String(row.break_name || '').trim() || 'Video';
  }
  if (isSupabasePublicVideoUrl(row.url)) {
    return String(row.break_name || '').trim() || 'Video';
  }
  if (isLikelyImageUrl(row.url)) {
    const file = imageFileLabelFromUrl(row.url);
    return file || String(row.break_name || '').trim() || 'Image';
  }
  return String(row.break_name || '').trim() || 'Media';
}

function normalizeMediaList(raw) {
  if (raw == null) return [];
  let data = raw;
  if (typeof raw === 'string') {
    try {
      data = JSON.parse(raw);
    } catch {
      const t = raw.trim();
      return t ? [t] : [];
    }
  }
  if (!Array.isArray(data)) return [];
  return data.map((u) => String(u ?? '').trim()).filter(Boolean);
}

/** `external_break_links` from API: [{ break_name, url }, ...] */
function normalizeExternalBreakLinks(raw) {
  if (raw == null) return [];
  let data = raw;
  if (typeof raw === 'string') {
    try {
      data = JSON.parse(raw);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(data)) {
    if (typeof data === 'object' && data !== null) {
      data = Object.entries(data).map(([k, v]) => ({
        break_name: k,
        url: String(v ?? ''),
      }));
    } else {
      return [];
    }
  }
  const out = [];
  for (const item of data) {
    if (!item || typeof item !== 'object') continue;
    const name = String(item.break_name ?? item.key ?? item.name ?? '').trim();
    const url = String(item.url ?? item.value ?? '').trim();
    if (!url) continue;
    out.push({ break_name: name || 'Break', url });
  }
  return out;
}

/** DB may store app-served paths (/assets/...) resolved against the API origin. */
function resolveCourseMediaUri(url) {
  if (!url || typeof url !== 'string') return url;
  const s = url.trim();
  if (/^https?:\/\//i.test(s)) return s;
  if (s.startsWith('/')) return `${getApiUrl()}${s}`;
  return s;
}

function descriptionToBulletLines(description) {
  if (description == null) return [];
  return String(description)
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
}

/** Strip script tags from stored HTML before rendering. */
function sanitizeStoryHtml(html) {
  if (html == null || typeof html !== 'string') return '';
  return html.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
}

function isRichHtmlDescription(s) {
  return /<\/?[a-z][a-z0-9]*\b/i.test(String(s));
}

function normalizeStoryImageList(raw) {
  if (Array.isArray(raw)) {
    return raw
      .filter((u) => typeof u === 'string' && u.trim().length > 0)
      .map((u) => u.trim());
  }
  if (typeof raw === 'string') {
    try {
      const p = JSON.parse(raw);
      return Array.isArray(p)
        ? p.filter((u) => typeof u === 'string' && u.trim()).map((u) => u.trim())
        : [];
    } catch {
      return [];
    }
  }
  return [];
}

/** Display name for story author line (full name or username or "Student"). */
function storyAuthorDisplayName(story) {
  const name =
    story.author_student_name != null ? String(story.author_student_name).trim() : '';
  const username = story.author_username != null ? String(story.author_username).trim() : '';
  return name || username || 'Student';
}

function StoryImagesStack({ urls, styles }) {
  if (!urls.length) return null;
  return (
    <View style={styles.storyImagesStack}>
      {urls.map((uri, i) => {
        const resolved = resolveCourseMediaUri(uri);
        return (
          <View key={`${resolved}-${i}`} style={styles.storyImageStackTile}>
            {Platform.OS === 'web' ? (
              <RNImage
                source={{ uri: resolved }}
                style={styles.storyImageStackImg}
                resizeMode="contain"
              />
            ) : (
              <Image
                source={{ uri: resolved }}
                style={styles.storyImageStackImg}
                contentFit="contain"
                transition={0}
              />
            )}
          </View>
        );
      })}
    </View>
  );
}

function StoryRichOrPlainBody({ description, theme, styles, contentWidth }) {
  const raw = description == null ? '' : String(description);
  const safe = sanitizeStoryHtml(raw);
  const wide = Math.max(160, contentWidth || 280);
  if (!safe.trim()) {
    return (
      <Text style={[styles.storyBulletBody, { color: theme.colors.text, opacity: 0.65 }]}>No description.</Text>
    );
  }
  if (!isRichHtmlDescription(safe)) {
    const lines = descriptionToBulletLines(safe);
    if (lines.length === 0) {
      return (
        <Text style={[styles.storyBulletBody, { color: theme.colors.text, opacity: 0.65 }]}>No description.</Text>
      );
    }
    return lines.map((line, idx) => (
      <View key={`L${idx}`} style={styles.storyBulletLine}>
        <Text style={[styles.storyBulletChar, { color: theme.colors.text }]}>•</Text>
        <Text style={[styles.storyBulletBody, { color: theme.colors.text }]}>{line}</Text>
      </View>
    ));
  }
  return (
    <RenderHtml
      contentWidth={wide}
      source={{ html: safe }}
      baseStyle={{
        color: theme.colors.text,
        fontSize: 15,
        lineHeight: 22,
      }}
      tagsStyles={{
        body: { margin: 0 },
        p: { marginTop: 0, marginBottom: 8 },
        ul: { marginBottom: 8 },
        ol: { marginBottom: 8 },
        li: { marginBottom: 4 },
        h1: { fontSize: 20, marginBottom: 8 },
        h2: { fontSize: 18, marginBottom: 6 },
        a: { color: theme.colors.primary || '#d4af37', textDecorationLine: 'underline' },
      }}
    />
  );
}

function CourseStoriesSection({
  stories,
  theme,
  styles,
  navigation,
  user,
  courseId,
  courseName,
  canAddStory = true,
}) {
  const [bodyColWidth, setBodyColWidth] = useState(300);
  const yellow = theme.colors.heading;

  useEffect(() => {
    if (!__DEV__) return;
    console.log('[MyCourseDetail/CourseStoriesSection]', {
      storiesCount: stories?.length ?? 0,
      courseId,
      courseName,
    });
  }, [stories, courseId, courseName]);

  const openStoryBuilder = useCallback(() => {
    if (!navigation || user == null || courseId == null) return;
    navigation.navigate('StoryBuilder', {
      user,
      initialCourseId: courseId,
      initialCourseName: courseName ?? '',
    });
  }, [navigation, user, courseId, courseName]);

  return (
    <View
      style={[styles.storiesOuterBox, { borderColor: yellow }]}
      {...(Platform.OS === 'web' ? { dataSet: { courseStoriesSection: 'true' } } : {})}
    >
      <Text style={[styles.storiesSectionHeading, { color: theme.colors.heading }]}>Stories</Text>
      {canAddStory ? (
        <TouchableOpacity
          style={styles.addStoryHereButton}
          onPress={openStoryBuilder}
          activeOpacity={0.85}
          accessibilityRole="button"
          accessibilityLabel="Add story here"
        >
          <Text style={styles.addStoryHereButtonText}>Add story here</Text>
        </TouchableOpacity>
      ) : null}

      {!stories || stories.length === 0 ? (
        <Text style={[styles.storiesEmptyHint, { color: theme.colors.text }]}>No stories yet.</Text>
      ) : (
        stories.map((story, storyIdx) => {
          const isLast = storyIdx === stories.length - 1;
          const title =
            story.story_title != null && String(story.story_title).trim() !== ''
              ? String(story.story_title).trim()
              : null;
          const imageUrls = normalizeStoryImageList(story.story_images);
          return (
            <View
              key={String(story.id)}
              style={[
                styles.storyInnerBox,
                !isLast && {
                  borderBottomWidth: 2,
                  borderBottomColor: yellow,
                  marginBottom: 14,
                  paddingBottom: 14,
                },
                isLast && { marginBottom: 0 },
              ]}
            >
              <View
                style={styles.storyBlockColumn}
                onLayout={(e) => setBodyColWidth(e.nativeEvent.layout.width)}
              >
                {title ? (
                  <Text style={[styles.storyTitleText, { color: theme.colors.heading }]}>{title}</Text>
                ) : null}
                <Text style={[styles.storyAuthorColon, { color: theme.colors.text }]}>
                  <Text style={{ fontWeight: '700', color: yellow }}>Author: </Text>
                  {storyAuthorDisplayName(story)}
                </Text>
                <StoryImagesStack urls={imageUrls} styles={styles} />
                <StoryRichOrPlainBody
                  description={story.description}
                  theme={theme}
                  styles={styles}
                  contentWidth={bodyColWidth}
                />
              </View>
            </View>
          );
        })
      )}
    </View>
  );
}


function ExternalLinkThumb({ url, style }) {
  const id = parseYouTubeVideoId(url);
  if (id) {
    const thumb = youtubeThumbUrl(id);
    return (
      <Image source={{ uri: thumb }} style={style} contentFit="cover" transition={0} />
    );
  }
  if (isSupabasePublicVideoUrl(url)) {
    return (
      <View
        style={[
          style,
          { backgroundColor: '#000', justifyContent: 'center', alignItems: 'center' },
        ]}
      />
    );
  }
  if (isLikelyImageUrl(url)) {
    const resolved = resolveCourseMediaUri(url);
    return (
      <Image source={{ uri: resolved }} style={style} contentFit="cover" transition={0} />
    );
  }
  return (
    <View
      style={[
        style,
        { backgroundColor: '#2a2a2a', justifyContent: 'center', alignItems: 'center' },
      ]}
    >
      <Text style={{ color: '#bbb', fontSize: 22 }}>▶</Text>
    </View>
  );
}

function CourseResourceLinks({ theme, styles, hasExternalBreaksAbove }) {
  if (!COURSE_RESOURCE_LINKS.length) return null;
  const openUrl = useCallback(async (raw) => {
    const url = String(raw || '').trim();
    if (!url) return;
    try {
      if (await Linking.canOpenURL(url)) {
        await Linking.openURL(url);
      }
    } catch {
      /* ignore */
    }
  }, []);

  return (
    <View
      style={[
        styles.resourceLinksSection,
        !hasExternalBreaksAbove && styles.resourceLinksSectionStandalone,
        {
          borderTopColor: theme.isDarkMode ? 'rgba(255,255,255,0.12)' : '#e0e0e0',
        },
      ]}
    >
      {COURSE_RESOURCE_LINKS.map((item) => {
        const hasUrl = String(item.url || '').trim().length > 0;
        const labelEl = (
          <Text
            style={[
              styles.resourceLinkLabel,
              {
                color: hasUrl ? theme.colors.heading : theme.colors.text,
                opacity: hasUrl ? 1 : 0.5,
              },
            ]}
          >
            {item.label}
          </Text>
        );
        return (
          <View
            key={item.label}
            style={[
              styles.resourceLinkRow,
              {
                borderBottomColor: theme.isDarkMode
                  ? 'rgba(255,255,255,0.08)'
                  : '#eee',
              },
            ]}
          >
            {hasUrl ? (
              <TouchableOpacity
                style={styles.resourceLinkHit}
                onPress={() => openUrl(item.url)}
                activeOpacity={0.7}
                accessibilityRole="link"
                accessibilityLabel={item.label}
              >
                {labelEl}
                <Text style={[styles.resourceLinkChevron, { color: theme.colors.heading }]}>›</Text>
              </TouchableOpacity>
            ) : (
              <View style={styles.resourceLinkHit} accessibilityRole="text">
                {labelEl}
              </View>
            )}
          </View>
        );
      })}
    </View>
  );
}

/** Same layout as course media carousel; no auto-advance. Thumbnail strip below selects slides. */
export function ExternalBreaksCarousel({
  links,
  theme,
  styles,
  slideWidth,
  slideHeight,
  onOpenVideo,
  editable = false,
  onRemoveBreak,
  hideSectionHeading = false,
  compactSection = false,
  removeButtonDataSet,
  sectionHeading = 'Course breaks',
}) {
  const flatListRef = useRef(null);
  const indexRef = useRef(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const showArrows = links.length > 1;

  const scrollToIndex = useCallback(
    (index) => {
      if (!links.length) return;
      const i = ((index % links.length) + links.length) % links.length;
      indexRef.current = i;
      setActiveIndex(i);
      if (Platform.OS === 'web') {
        /* Main stage is a single View (not ScrollView) so expo-image paints; index state drives the slide. */
      } else {
        try {
          flatListRef.current?.scrollToIndex({ index: i, animated: true });
        } catch {
          flatListRef.current?.scrollToOffset({
            offset: i * slideWidth,
            animated: true,
          });
        }
      }
    },
    [links.length, slideWidth]
  );

  useEffect(() => {
    indexRef.current = 0;
    setActiveIndex(0);
    if (Platform.OS !== 'web') {
      flatListRef.current?.scrollToOffset({ offset: 0, animated: false });
    }
  }, [links.length, links[0]?.url]);

  const goPrev = useCallback(() => {
    if (!showArrows) return;
    scrollToIndex(indexRef.current - 1);
  }, [showArrows, scrollToIndex]);

  const goNext = useCallback(() => {
    if (!showArrows) return;
    scrollToIndex(indexRef.current + 1);
  }, [showArrows, scrollToIndex]);

  const onMomentumEnd = useCallback(
    (e) => {
      const x = e.nativeEvent.contentOffset.x;
      const raw = Math.round(x / Math.max(slideWidth, 1));
      const i = Math.min(links.length - 1, Math.max(0, raw));
      indexRef.current = i;
      setActiveIndex(i);
    },
    [slideWidth, links.length]
  );

  const wrapSlideRemove = useCallback(
    (slideEl, row, slideIndex) => {
      if (!editable || !onRemoveBreak) return slideEl;
      return (
        <View style={{ width: slideWidth, height: slideHeight, position: 'relative' }}>
          {slideEl}
          <TouchableOpacity
            style={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 20,
              width: 30,
              height: 30,
              borderRadius: 15,
              backgroundColor: theme.isDarkMode
                ? 'rgba(0,0,0,0.55)'
                : theme.colors.primary || '#d4af37',
              justifyContent: 'center',
              alignItems: 'center',
            }}
            onPress={() => onRemoveBreak(row, slideIndex)}
            accessibilityRole="button"
            accessibilityLabel="Remove break media"
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            {...(Platform.OS === 'web' && removeButtonDataSet ? { dataSet: removeButtonDataSet } : {})}
          >
            <Text style={{ color: '#fff', fontSize: 18, fontWeight: '700', lineHeight: 20 }}>✕</Text>
          </TouchableOpacity>
        </View>
      );
    },
    [editable, onRemoveBreak, slideWidth, slideHeight, theme.isDarkMode, theme.colors.primary, removeButtonDataSet]
  );

  const renderSlide = useCallback(
    (row, slideIndex = 0) => {
      const url = row.url;
      const videoId = parseYouTubeVideoId(url);
      const storageVideo = isSupabasePublicVideoUrl(url);
      const open = () => {
        const isImg = isLikelyImageUrl(url);
        onOpenVideo(resolveExternalPlaybackUri(url), row.break_name || externalLinkStripLabel(row), {
          isImage: isImg,
        });
      };
      let slideEl;
      if (videoId || storageVideo) {
        slideEl = (
          <TouchableOpacity
            style={[styles.carouselSlide, { width: slideWidth, height: slideHeight }]}
            onPress={open}
            activeOpacity={0.9}
            accessibilityRole="button"
            accessibilityLabel={`Play ${row.break_name || 'video'}`}
          >
            <View
              style={[
                styles.extMainSlideThumb,
                { width: slideWidth, height: slideHeight },
              ]}
            >
              <ExternalLinkThumb url={url} style={StyleSheet.absoluteFillObject} />
            </View>
            <View style={styles.carouselVideoOverlay} pointerEvents="none">
              <View style={styles.carouselPlayCircle}>
                <Text style={styles.carouselPlayTriangle}>▶</Text>
              </View>
            </View>
          </TouchableOpacity>
        );
      } else {
        slideEl = (
          <TouchableOpacity
            style={[styles.carouselSlide, { width: slideWidth, height: slideHeight }]}
            onPress={open}
            activeOpacity={0.9}
            accessibilityRole="button"
            accessibilityLabel={`Open ${externalLinkStripLabel(row)}`}
          >
            <ExternalLinkThumb url={url} style={StyleSheet.absoluteFill} />
          </TouchableOpacity>
        );
      }
      return wrapSlideRemove(slideEl, row, slideIndex);
    },
    [slideWidth, slideHeight, onOpenVideo, styles, wrapSlideRemove]
  );

  if (!links.length) return null;

  const arrowSpacer = !showArrows ? (
    <View style={{ width: CAROUSEL_ARROW_HIT, height: 1 }} />
  ) : null;

  const listWebStyle = [styles.carouselListWeb, { width: slideWidth, height: slideHeight }];

  const headingEl =
    !hideSectionHeading ? (
      <Text style={[styles.extHeading, { color: theme.colors.heading }]}>{sectionHeading}</Text>
    ) : null;

  const carouselOuter = (
      <View style={styles.extCarouselOuter}>
        <View style={styles.carouselRow}>
          {showArrows ? (
            <TouchableOpacity
              style={styles.carouselArrowHit}
              onPress={goPrev}
              accessibilityRole="button"
              accessibilityLabel="Previous break"
              hitSlop={{ top: 12, bottom: 12, left: 8, right: 8 }}
            >
              <Text style={[styles.carouselArrowText, { color: theme.colors.heading }]}>‹</Text>
            </TouchableOpacity>
          ) : (
            arrowSpacer
          )}
          <View
            style={[styles.carouselStage, { width: slideWidth }]}
            dataSet={{ externalBreaksCarousel: 'true' }}
          >
            {Platform.OS === 'web' ? (
              <View style={listWebStyle}>
                {links[activeIndex] ? (
                  <View
                    key={`${activeIndex}-${links[activeIndex].url}`}
                    style={{ width: slideWidth, height: slideHeight }}
                  >
                    {renderSlide(links[activeIndex], activeIndex)}
                  </View>
                ) : null}
              </View>
            ) : (
              <FlatList
                ref={flatListRef}
                data={links}
                horizontal
                pagingEnabled
                showsHorizontalScrollIndicator={false}
                style={listWebStyle}
                keyExtractor={(item, index) => `${index}-${item.url}`}
                getItemLayout={(_, index) => ({
                  length: slideWidth,
                  offset: slideWidth * index,
                  index,
                })}
                onMomentumScrollEnd={onMomentumEnd}
                onScrollToIndexFailed={(info) => {
                  setTimeout(() => {
                    flatListRef.current?.scrollToIndex({
                      index: info.index,
                      animated: true,
                    });
                  }, 100);
                }}
                renderItem={({ item, index }) => renderSlide(item, index)}
                nestedScrollEnabled
              />
            )}
          </View>
          {showArrows ? (
            <TouchableOpacity
              style={styles.carouselArrowHit}
              onPress={goNext}
              accessibilityRole="button"
              accessibilityLabel="Next break"
              hitSlop={{ top: 12, bottom: 12, left: 8, right: 8 }}
            >
              <Text style={[styles.carouselArrowText, { color: theme.colors.heading }]}>›</Text>
            </TouchableOpacity>
          ) : (
            arrowSpacer
          )}
        </View>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          nestedScrollEnabled
          style={styles.extThumbStrip}
          contentContainerStyle={styles.extThumbStripContent}
          {...(Platform.OS === 'web' ? { dataSet: { externalBreakThumbs: 'true' } } : {})}
        >
          {links.map((row, i) => {
            const label = externalLinkStripLabel(row);
            const selected = i === activeIndex;
            const thumbBody = (
              <>
                <View
                  style={[
                    styles.extThumbTileFrame,
                    {
                      borderColor: selected
                        ? theme.colors.heading
                        : theme.isDarkMode
                          ? theme.colors.border
                          : '#ccc',
                    },
                  ]}
                >
                  <View style={styles.extThumbTileImage}>
                    <ExternalLinkThumb url={row.url} style={styles.extThumbFill} />
                  </View>
                </View>
                <Text
                  style={[styles.extThumbTileCaption, { color: theme.colors.text }]}
                  numberOfLines={2}
                >
                  {label}
                </Text>
              </>
            );
            return (
              <View
                key={`${i}-${row.url}`}
                style={[styles.extThumbTile, { position: 'relative' }]}
              >
                <TouchableOpacity
                  onPress={() => scrollToIndex(i)}
                  activeOpacity={0.85}
                  accessibilityRole="button"
                  accessibilityState={{ selected }}
                  accessibilityLabel={`Show ${label}`}
                  style={{ width: '100%', alignItems: 'center' }}
                >
                  {thumbBody}
                </TouchableOpacity>
                {editable && onRemoveBreak ? (
                  <TouchableOpacity
                    style={{
                      position: 'absolute',
                      top: -4,
                      right: -2,
                      zIndex: 15,
                      width: 24,
                      height: 24,
                      borderRadius: 12,
                      backgroundColor: theme.isDarkMode
                        ? 'rgba(0,0,0,0.55)'
                        : theme.colors.primary || '#d4af37',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                    onPress={() => onRemoveBreak(row, i)}
                    accessibilityRole="button"
                    accessibilityLabel="Remove break media"
                    hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
                    {...(Platform.OS === 'web' && removeButtonDataSet ? { dataSet: removeButtonDataSet } : {})}
                  >
                    <Text style={{ color: '#fff', fontSize: 14, fontWeight: '700', lineHeight: 16 }}>✕</Text>
                  </TouchableOpacity>
                ) : null}
              </View>
            );
          })}
        </ScrollView>
      </View>
  );

  if (compactSection) {
    return (
      <>
        {headingEl}
        {carouselOuter}
      </>
    );
  }

  return (
    <View style={styles.extSection}>
      {headingEl}
      {carouselOuter}
    </View>
  );
}

export default function MyCourseDetailPage({ navigation, route }) {
  const theme = useTheme();
  const stopwatchRef = useRef(null);
  const { width: windowWidth } = useWindowDimensions();
  const user = route?.params?.user ?? null;
  const courseId = route?.params?.courseId ?? null;
  const courseName = route?.params?.courseName ?? 'Course';
  const isEnrolledInCourse = useMemo(
    () => viewerHasCourseEnrollment(user, courseName),
    [user, courseName]
  );
  const canAddCourseStories = useMemo(() => canCreateCourseStories(user), [user]);

  const [loading, setLoading] = useState(true);
  const [percentComplete, setPercentComplete] = useState(0);
  const [breaksError, setBreaksError] = useState(false);
  const [fatalError, setFatalError] = useState(false);
  const [mediaUrls, setMediaUrls] = useState([]);
  const [courseSummary, setCourseSummary] = useState(null);
  const [externalBreakLinks, setExternalBreakLinks] = useState([]);
  const [courseStories, setCourseStories] = useState([]);
  const [videoModal, setVideoModal] = useState(null);

  const slideWidth = Math.max(1, Math.round(windowWidth * CAROUSEL_WIDTH_RATIO));
  const slideHeight = Math.round(slideWidth / CAROUSEL_ASPECT);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!user?.id || courseId == null) {
        setLoading(false);
        setFatalError(true);
        setMediaUrls([]);
        setCourseSummary(null);
        setExternalBreakLinks([]);
        setCourseStories([]);
        setPercentComplete(0);
        return;
      }
      setLoading(true);
      setFatalError(false);
      setBreaksError(false);

      const courseUrl = `${getApiUrl()}/api/courses/${encodeURIComponent(String(courseId))}`;
      const breaksUrl = isEnrolledInCourse
        ? `${getApiUrl()}/api/courses/${encodeURIComponent(String(courseId))}/breaks/${encodeURIComponent(String(user.id))}`
        : null;

      const [courseOutcome, breaksOutcome] = await Promise.allSettled([
        fetch(courseUrl).then(async (r) => {
          if (!r.ok) throw new Error(String(r.status));
          return r.json();
        }),
        breaksUrl
          ? fetch(breaksUrl).then(async (r) => {
              if (!r.ok) throw new Error(String(r.status));
              return r.json();
            })
          : Promise.resolve(null),
      ]);

      if (cancelled) return;

      if (courseOutcome.status === 'fulfilled') {
        const row = courseOutcome.value;
        setMediaUrls(normalizeMediaList(row?.course_media_display));
        const rawSum = row?.summary;
        const sum =
          rawSum == null || String(rawSum).trim() === ''
            ? null
            : String(rawSum).trim();
        setCourseSummary(sum);
        setExternalBreakLinks(normalizeExternalBreakLinks(row?.external_break_links));
      } else {
        setMediaUrls([]);
        setCourseSummary(null);
        setExternalBreakLinks([]);
      }

      if (breaksOutcome.status === 'fulfilled' && breaksOutcome.value != null) {
        setPercentComplete(computeCourseCompletionPercent(breaksOutcome.value));
        setBreaksError(false);
      } else if (!isEnrolledInCourse) {
        setPercentComplete(0);
        setBreaksError(false);
      } else {
        setPercentComplete(0);
        setBreaksError(true);
      }

      setLoading(false);
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [user?.id, courseId, isEnrolledInCourse]);

  useFocusEffect(
    useCallback(() => {
      let cancelled = false;
      async function loadStories() {
        if (courseId == null) {
          if (!cancelled) setCourseStories([]);
          return;
        }
        const storiesUrl = `${getApiUrl()}/api/courses/${encodeURIComponent(String(courseId))}/stories`;
        if (__DEV__) {
          console.log('[MyCourseDetail/stories] fetch', { storiesUrl, courseId, courseName });
        }
        try {
          const r = await fetch(storiesUrl);
          if (!r.ok) {
            if (__DEV__) {
              console.warn('[MyCourseDetail/stories] HTTP not OK', {
                status: r.status,
                statusText: r.statusText,
                storiesUrl,
              });
            }
            if (!cancelled) setCourseStories([]);
            return;
          }
          const ct = r.headers.get('content-type') || '';
          if (!ct.includes('application/json')) {
            if (__DEV__) {
              console.warn('[MyCourseDetail/stories] unexpected content-type', {
                contentType: ct,
                storiesUrl,
              });
            }
            if (!cancelled) setCourseStories([]);
            return;
          }
          const data = await r.json();
          const list = Array.isArray(data) ? data : [];
          if (__DEV__) {
            console.log('[MyCourseDetail/stories] response', {
              count: list.length,
              rows: list.map((s) => ({
                id: s.id,
                course_id: s.course_id,
                associated_course_name: s.associated_course_name,
              })),
            });
          }
          if (!cancelled) setCourseStories(list);
        } catch (err) {
          if (__DEV__) {
            console.warn('[MyCourseDetail/stories] fetch error', {
              message: err?.message,
              storiesUrl,
            });
          }
          if (!cancelled) setCourseStories([]);
        }
      }
      loadStories();
      return () => {
        cancelled = true;
      };
    }, [courseId, courseName])
  );

  const storiesForThisCourse = useMemo(() => {
    if (!Array.isArray(courseStories)) return [];
    const target = normalizeCourseDisplayName(courseName);
    const cid = courseId != null ? Number(courseId) : NaN;
    return courseStories.filter((s) => {
      const sid = s.course_id != null ? Number(s.course_id) : NaN;
      if (!Number.isNaN(cid) && !Number.isNaN(sid) && sid === cid) return true;
      const assoc = normalizeCourseDisplayName(s.associated_course_name);
      return (
        Boolean(target) &&
        Boolean(assoc) &&
        assoc.localeCompare(target, undefined, { sensitivity: 'base' }) === 0
      );
    });
  }, [courseStories, courseName, courseId]);

  useEffect(() => {
    if (!__DEV__) return;
    const target = normalizeCourseDisplayName(courseName);
    const cid = courseId != null ? Number(courseId) : NaN;
    const raw = Array.isArray(courseStories) ? courseStories : [];
    const keptIds = new Set(storiesForThisCourse.map((s) => s.id));
    const dropped = raw.filter((s) => !keptIds.has(s.id));
    console.log('[MyCourseDetail/stories] filter snapshot', {
      routeCourseId: courseId,
      numericCourseId: cid,
      routeCourseName: courseName,
      normalizedRouteName: target,
      rawCount: raw.length,
      filteredCount: storiesForThisCourse.length,
      filteredIds: storiesForThisCourse.map((s) => s.id),
    });
    if (dropped.length > 0) {
      console.warn(
        '[MyCourseDetail/stories] rows dropped by client filter',
        dropped.map((s) => ({
          id: s.id,
          course_id: s.course_id,
          course_id_num: s.course_id != null ? Number(s.course_id) : NaN,
          associated_course_name: s.associated_course_name,
          normalizedAssoc: normalizeCourseDisplayName(s.associated_course_name),
          matchedByCourseId:
            !Number.isNaN(cid) &&
            !Number.isNaN(Number(s.course_id)) &&
            Number(s.course_id) === cid,
          matchedByName:
            Boolean(target) &&
            Boolean(normalizeCourseDisplayName(s.associated_course_name)) &&
            normalizeCourseDisplayName(s.associated_course_name).localeCompare(target, undefined, {
              sensitivity: 'base',
            }) === 0,
        }))
      );
    }
  }, [courseStories, storiesForThisCourse, courseId, courseName]);

  const resolvedCourseGalleryUrls = useMemo(
    () => mediaUrls.map((e) => resolveCourseGalleryEntryToUrl(e)).filter(Boolean),
    [mediaUrls]
  );

  const styles = useMemo(() => getStyles(theme, slideWidth), [theme, slideWidth]);

  const goMyCourses = () => {
    if (user) navigation.navigate('MyCourses', { user });
    else navigation.goBack();
  };

  const openVideoModal = useCallback((uri, title = 'Video', options = {}) => {
    if (!uri) return;
    setVideoModal({
      uri,
      title: title || 'Video',
      isImage: !!options.isImage,
    });
  }, []);

  const closeVideoModal = useCallback(() => setVideoModal(null), []);

  return (
    <View
      style={styles.container}
      data-theme={theme.isDarkMode ? 'dark' : 'light'}
      {...(Platform.OS === 'web' ? { dataSet: { screen: 'my-course-detail' } } : {})}
    >
      <StatusBar style={theme.isDarkMode ? 'light' : 'dark'} />
      <View style={styles.header} data-header="true">
        <View style={styles.hamburgerContainer} data-header-element="hamburger">
          <HamburgerMenu navigation={navigation} stopwatchRef={stopwatchRef} />
        </View>
        <TouchableOpacity
          style={styles.breadcrumbContainer}
          onPress={goMyCourses}
          activeOpacity={0.7}
          accessibilityRole="button"
          accessibilityLabel="Back to My Courses"
        >
          <Text style={styles.breadcrumbBack} numberOfLines={1}>
            ‹ My Courses
          </Text>
        </TouchableOpacity>
        <View style={styles.profileIconContainer} data-header-element="profile">
          <ProfileIcon navigation={navigation} user={user} />
        </View>
      </View>

      <ScrollView
        style={styles.content}
        contentContainerStyle={[
          styles.contentContainer,
          Platform.OS === 'web' && styles.contentContainerWeb,
        ]}
        showsVerticalScrollIndicator={false}
        nestedScrollEnabled
        {...(Platform.OS === 'web' ? { dataSet: { courseDetailBody: 'true' } } : {})}
      >
        {loading ? (
          <View style={styles.centerBlock}>
            <ActivityIndicator size="large" color={theme.colors.heading} />
          </View>
        ) : fatalError ? (
          <View style={styles.centerBlock}>
            <Text style={[styles.errorTitle, { color: theme.colors.heading }]}>
              Couldn&apos;t open this course
            </Text>
            <Text style={[styles.errorSub, { color: theme.colors.text }]}>
              Go back to My Courses and try again.
            </Text>
          </View>
        ) : (
          <>
            <View style={styles.titleRow}>
              <Text
                style={[styles.courseTitle, { color: theme.colors.heading }]}
                numberOfLines={3}
              >
                {courseName}
              </Text>
              {isEnrolledInCourse ? (
                breaksError ? (
                  <Text
                    style={[
                      styles.percentInlineMuted,
                      { color: theme.colors.text, marginLeft: 10 },
                    ]}
                  >
                    Progress unavailable
                  </Text>
                ) : (
                  <Text
                    style={[
                      styles.percentInline,
                      { color: theme.colors.text, marginLeft: 10 },
                    ]}
                  >
                    [{percentComplete}% complete]
                  </Text>
                )
              ) : null}
            </View>
            <CourseMediaCarousel
              mediaUrls={resolvedCourseGalleryUrls}
              slideWidth={slideWidth}
              slideHeight={slideHeight}
              theme={theme}
              styles={styles}
              onOpenVideo={openVideoModal}
              pauseAutoRotate={!!videoModal}
            />
            <View style={styles.summaryBlock}>
              <Text style={[styles.summaryHeading, { color: theme.colors.heading }]}>
                Course Summary
              </Text>
              <Text
                style={[
                  styles.summaryBody,
                  {
                    color: theme.colors.text,
                    opacity: courseSummary ? 1 : 0.65,
                  },
                ]}
              >
                {courseSummary && String(courseSummary).trim() !== ''
                  ? courseSummary
                  : 'No summary has been added for this course yet.'}
              </Text>
            </View>
            {externalBreakLinks.length > 0 ? (
              <ExternalBreaksCarousel
                links={externalBreakLinks}
                theme={theme}
                styles={styles}
                slideWidth={slideWidth}
                slideHeight={slideHeight}
                onOpenVideo={openVideoModal}
                sectionHeading={isEnrolledInCourse ? 'Course breaks' : 'Course videos'}
              />
            ) : null}
            {!isEnrolledInCourse ? (
              <View
                style={[
                  styles.lockedCourseBanner,
                  { borderColor: COURSE_LOCKED_YELLOW },
                ]}
                {...(Platform.OS === 'web' ? { dataSet: { courseLockedBanner: 'true' } } : {})}
                accessibilityRole="text"
                accessibilityLabel="This course is locked."
              >
                <MaterialIcons
                  name="lock"
                  size={22}
                  color={COURSE_LOCKED_YELLOW}
                  accessibilityElementsHidden
                  importantForAccessibility="no"
                />
                <Text
                  style={[
                    styles.lockedCourseText,
                    {
                      color: theme.isDarkMode ? COURSE_LOCKED_YELLOW : COURSE_LOCKED_GOLD,
                    },
                  ]}
                >
                  This course is locked.
                </Text>
              </View>
            ) : null}
            {showChiGungHandbookOnCourseDetail(user, courseName) ||
            showDimMakHandbookOnCourseDetail(user, courseName) ? (
              <View
                style={[
                  styles.resourceLinksSection,
                  !externalBreakLinks.length && styles.resourceLinksSectionStandalone,
                  {
                    borderTopColor: theme.isDarkMode ? 'rgba(255,255,255,0.12)' : '#e0e0e0',
                  },
                ]}
              >
                <View
                  style={[
                    styles.resourceLinkRow,
                    {
                      borderBottomColor: theme.isDarkMode ? 'rgba(255,255,255,0.08)' : '#eee',
                    },
                  ]}
                >
                  <TouchableOpacity
                    style={styles.resourceLinkHit}
                    onPress={() =>
                      navigation.navigate(
                        showDimMakHandbookOnCourseDetail(user, courseName)
                          ? 'DimMakHandbook'
                          : 'ChiGungHandbook',
                        {
                          user,
                          courseId,
                          courseName: normalizeCourseDisplayName(courseName),
                        }
                      )
                    }
                    activeOpacity={0.7}
                    accessibilityRole="link"
                    accessibilityLabel={`${normalizeCourseDisplayName(courseName)} handbook`}
                  >
                    <Text style={[styles.resourceLinkLabel, { color: theme.colors.heading }]}>
                      {normalizeCourseDisplayName(courseName)} handbook
                    </Text>
                    <Text style={[styles.resourceLinkChevron, { color: theme.colors.heading }]}>›</Text>
                  </TouchableOpacity>
                </View>
              </View>
            ) : null}
            {isEnrolledInCourse ? (
              <CourseResourceLinks
                theme={theme}
                styles={styles}
                hasExternalBreaksAbove={externalBreakLinks.length > 0}
              />
            ) : null}
            <CourseStoriesSection
              stories={storiesForThisCourse}
              theme={theme}
              styles={styles}
              navigation={navigation}
              user={user}
              courseId={courseId}
              courseName={courseName}
              canAddStory={canAddCourseStories}
            />
          </>
        )}
      </ScrollView>

      <Modal
        visible={!!videoModal}
        animationType="fade"
        transparent
        onRequestClose={closeVideoModal}
        supportedOrientations={['portrait', 'landscape']}
      >
        <View style={styles.ytModalRoot}>
          <Pressable style={styles.ytModalBackdrop} onPress={closeVideoModal} />
          <View
            style={[
              styles.ytModalSheet,
              {
                backgroundColor: theme.isDarkMode ? theme.colors.surface : '#111',
                borderColor: theme.isDarkMode ? theme.colors.border : '#333',
              },
            ]}
          >
            <View style={styles.ytModalHeader}>
              <Text
                style={[styles.ytModalTitle, { color: theme.colors.heading }]}
                numberOfLines={2}
              >
                {videoModal?.title ?? 'Video'}
              </Text>
              <TouchableOpacity
                onPress={closeVideoModal}
                style={styles.ytModalClose}
                hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
                accessibilityRole="button"
                accessibilityLabel="Close video"
              >
                <Text
                  style={[
                    styles.ytModalCloseText,
                    { color: theme.isDarkMode ? theme.colors.text : '#ffffff' },
                  ]}
                >
                  ✕
                </Text>
              </TouchableOpacity>
            </View>
            <View style={styles.ytWebViewWrap}>
              {videoModal?.uri ? (
                videoModal.isImage ? (
                  Platform.OS === 'web' ? (
                    <RNImage
                      source={{ uri: videoModal.uri }}
                      style={styles.ytModalImage}
                      resizeMode="contain"
                    />
                  ) : (
                    <Image
                      source={{ uri: videoModal.uri }}
                      style={styles.ytModalImage}
                      contentFit="contain"
                      transition={0}
                    />
                  )
                ) : (
                  <ModalVideoEmbed
                    uri={videoModal.uri}
                    title={videoModal.title}
                    webViewStyle={styles.ytWebView}
                  />
                )
              ) : null}
            </View>
          </View>
        </View>
      </Modal>

      <Stopwatch ref={stopwatchRef} />
    </View>
  );
}

function getStyles(theme, slideWidth) {
  const slideHeight = Math.round(slideWidth / CAROUSEL_ASPECT);
  return StyleSheet.create({
    ...createCourseMediaCarouselStyles(theme, slideWidth, slideHeight),
    container: {
      flex: 1,
      backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingTop: 50,
      paddingBottom: 20,
      paddingHorizontal: 20,
      backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
    },
    hamburgerContainer: {
      marginRight: 15,
      backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
    },
    breadcrumbContainer: {
      flex: 1,
      backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
      minWidth: 0,
    },
    breadcrumbBack: {
      fontSize: 17,
      fontWeight: '600',
      color: theme.colors.heading,
    },
    profileIconContainer: {
      backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
    },
    content: { flex: 1 },
    contentContainer: {
      paddingHorizontal: 20,
      paddingTop: 24,
      paddingBottom: 40,
    },
    contentContainerWeb: Platform.select({
      web: { flexGrow: 1, minHeight: 'calc(100vh - 120px)' },
      default: {},
    }),
    centerBlock: {
      paddingVertical: 48,
      alignItems: 'center',
      justifyContent: 'center',
    },
    titleRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      alignItems: 'baseline',
      marginBottom: 16,
    },
    lockedCourseBanner: {
      flexDirection: 'row',
      alignItems: 'center',
      borderWidth: 1,
      borderRadius: 5,
      paddingVertical: 12,
      paddingHorizontal: 14,
      marginBottom: 16,
      backgroundColor: 'transparent',
    },
    lockedCourseText: {
      flex: 1,
      marginLeft: 10,
      fontSize: 15,
      fontWeight: '600',
      backgroundColor: 'transparent',
    },
    courseTitle: {
      fontSize: 26,
      fontWeight: '700',
      flexShrink: 1,
    },
    percentInline: {
      fontSize: 18,
      fontWeight: '500',
    },
    percentInlineMuted: {
      fontSize: 16,
      fontWeight: '500',
      fontStyle: 'italic',
    },
    summaryBlock: {
      marginBottom: 18,
      alignSelf: 'stretch',
    },
    summaryHeading: {
      fontSize: 17,
      fontWeight: '700',
      marginBottom: 8,
    },
    summaryBody: {
      fontSize: 15,
      lineHeight: 22,
    },
    extSection: {
      marginTop: 12,
      marginBottom: 24,
      alignSelf: 'stretch',
    },
    extHeading: {
      fontSize: 17,
      fontWeight: '700',
      marginBottom: 10,
    },
    extCarouselOuter: {
      marginBottom: 4,
      alignSelf: 'stretch',
      width: '100%',
    },
    extThumbStrip: {
      marginTop: 12,
      width: '100%',
      alignSelf: 'stretch',
    },
    /** Mobile-first: when thumbs fit, row is centered; when many, horizontal scroll. */
    extThumbStripContent: {
      flexDirection: 'row',
      flexWrap: 'nowrap',
      alignItems: 'flex-start',
      justifyContent: 'center',
      flexGrow: 1,
      minWidth: '100%',
      paddingVertical: 8,
      paddingHorizontal: 4,
    },
    extThumbTile: {
      width: EXT_THUMB_TILE_WIDTH,
      marginHorizontal: EXT_THUMB_TILE_GAP / 2,
      alignItems: 'center',
    },
    extThumbTileFrame: {
      width: '100%',
      borderRadius: 8,
      borderWidth: 2,
      overflow: 'hidden',
      backgroundColor: theme.isDarkMode ? '#1a1a1a' : '#e8e8e8',
    },
    extThumbTileImage: {
      position: 'relative',
      width: '100%',
      aspectRatio: CAROUSEL_ASPECT,
    },
    extThumbFill: {
      ...StyleSheet.absoluteFillObject,
    },
    /** Same pattern as extThumbTileImage + extThumbFill (working strip); explicit WxH for main stage. */
    extMainSlideThumb: {
      position: 'relative',
      overflow: 'hidden',
      backgroundColor: theme.isDarkMode ? '#1a1a1a' : '#e8e8e8',
    },
    extThumbTileCaption: {
      fontSize: 11,
      lineHeight: 14,
      marginTop: 6,
      textAlign: 'center',
      width: '100%',
    },
    resourceLinksSection: {
      marginTop: 8,
      paddingTop: 16,
      alignSelf: 'stretch',
      borderTopWidth: StyleSheet.hairlineWidth,
    },
    resourceLinksSectionStandalone: {
      marginTop: 20,
    },
    resourceLinkRow: {
      borderBottomWidth: StyleSheet.hairlineWidth,
    },
    resourceLinkHit: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingVertical: 14,
      paddingHorizontal: 2,
    },
    resourceLinkLabel: {
      fontSize: 16,
      fontWeight: '600',
      flex: 1,
    },
    resourceLinkChevron: {
      fontSize: 22,
      fontWeight: '300',
      marginLeft: 8,
    },
    storiesOuterBox: {
      marginTop: 18,
      alignSelf: 'stretch',
      borderWidth: 2,
      borderRadius: 10,
      padding: 12,
    },
    storiesSectionHeading: {
      fontSize: 17,
      fontWeight: '700',
      marginBottom: 10,
    },
    addStoryHereButton: {
      alignSelf: 'stretch',
      backgroundColor: '#d4af37',
      padding: 15,
      borderRadius: 8,
      alignItems: 'center',
      marginBottom: 12,
    },
    addStoryHereButtonText: {
      color: theme.isDarkMode ? '#FFFFFF' : '#000000',
      fontSize: 16,
      fontWeight: '600',
      textAlign: 'center',
    },
    storiesEmptyHint: {
      fontSize: 14,
      opacity: 0.72,
      marginBottom: 4,
      fontStyle: 'italic',
    },
    storyInnerBox: {
      width: '80%',
      alignSelf: 'center',
      borderWidth: 0,
      borderRadius: 0,
      paddingHorizontal: 8,
      paddingTop: 4,
    },
    storyBlockColumn: {
      width: '100%',
      alignSelf: 'stretch',
    },
    storyAuthorColon: {
      fontSize: 16,
      fontWeight: '700',
      marginBottom: 8,
      alignSelf: 'flex-start',
    },
    storyBulletLine: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      marginBottom: 6,
    },
    storyBulletChar: {
      width: 18,
      marginRight: 6,
      fontSize: 15,
      lineHeight: 22,
    },
    storyBulletBody: {
      flex: 1,
      fontSize: 15,
      lineHeight: 22,
    },
    storyTitleText: {
      fontSize: 17,
      fontWeight: '700',
      marginBottom: 8,
    },
    storyImagesStack: {
      width: '100%',
      marginBottom: 10,
    },
    storyImageStackTile: {
      width: '100%',
      height: 300,
      borderRadius: 8,
      overflow: 'hidden',
      marginBottom: 12,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.isDarkMode ? '#2a2a2a' : '#e8e8e8',
    },
    storyImageStackImg: {
      width: '100%',
      height: '100%',
    },
    errorTitle: {
      fontSize: 18,
      fontWeight: '600',
      marginBottom: 8,
      textAlign: 'center',
    },
    errorSub: {
      fontSize: 15,
      lineHeight: 22,
      textAlign: 'center',
      maxWidth: 320,
    },
    ytModalRoot: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      padding: 16,
    },
    ytModalBackdrop: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: 'rgba(0,0,0,0.65)',
    },
    ytModalSheet: {
      width: '100%',
      maxWidth: 720,
      borderRadius: 12,
      borderWidth: 1,
      overflow: 'hidden',
      zIndex: 2,
    },
    ytModalHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: 14,
      paddingVertical: 10,
    },
    ytModalTitle: {
      flex: 1,
      marginRight: 12,
      fontSize: 17,
      fontWeight: '600',
    },
    ytModalClose: {
      padding: 4,
      ...(theme.isDarkMode
        ? {}
        : {
            width: 34,
            height: 34,
            borderRadius: 17,
            backgroundColor: theme.colors.primary || '#d4af37',
            justifyContent: 'center',
            alignItems: 'center',
            padding: 0,
          }),
    },
    ytModalCloseText: {
      fontSize: 20,
      fontWeight: '600',
    },
    ytWebViewWrap: {
      width: '100%',
      height: Math.min(Math.round(slideWidth * 0.56), 400),
      minHeight: 220,
      backgroundColor: '#000',
    },
    ytWebView: {
      flex: 1,
      backgroundColor: '#000',
    },
    ytModalImage: {
      width: '100%',
      height: '100%',
      backgroundColor: '#000',
    },
  });
}

const CAROUSEL_ASPECT_EXPORT = 16 / 9;
const EXT_THUMB_W = 92;
const EXT_THUMB_GAP = 10;

/** Styles for {@link ExternalBreaksCarousel} outside this module (e.g. handbook comments). */
export function getExternalBreaksCarouselStylesBundle(theme, slideWidth) {
  const slideHeight = Math.round(slideWidth / CAROUSEL_ASPECT_EXPORT);
  return {
    ...createCourseMediaCarouselStyles(theme, slideWidth, slideHeight),
    extSection: {
      marginTop: 8,
      marginBottom: 12,
      alignSelf: 'stretch',
    },
    extHeading: {
      fontSize: 14,
      fontWeight: '700',
      marginBottom: 6,
    },
    extCarouselOuter: {
      marginBottom: 4,
      alignSelf: 'stretch',
      width: '100%',
    },
    extThumbStrip: {
      marginTop: 12,
      width: '100%',
      alignSelf: 'stretch',
    },
    extThumbStripContent: {
      flexDirection: 'row',
      flexWrap: 'nowrap',
      alignItems: 'flex-start',
      justifyContent: 'center',
      flexGrow: 1,
      minWidth: '100%',
      paddingVertical: 8,
      paddingHorizontal: 4,
    },
    extThumbTile: {
      width: EXT_THUMB_W,
      marginHorizontal: EXT_THUMB_GAP / 2,
      alignItems: 'center',
    },
    extThumbTileFrame: {
      width: '100%',
      borderRadius: 8,
      borderWidth: 2,
      overflow: 'hidden',
      backgroundColor: theme.isDarkMode ? '#1a1a1a' : '#e8e8e8',
    },
    extThumbTileImage: {
      position: 'relative',
      width: '100%',
      aspectRatio: CAROUSEL_ASPECT_EXPORT,
    },
    extThumbFill: StyleSheet.absoluteFillObject,
    extMainSlideThumb: {
      position: 'relative',
      overflow: 'hidden',
      backgroundColor: theme.isDarkMode ? '#1a1a1a' : '#e8e8e8',
    },
    extThumbTileCaption: {
      fontSize: 11,
      lineHeight: 14,
      marginTop: 6,
      textAlign: 'center',
      width: '100%',
    },
  };
}
